import React, { useCallback, useEffect, useState } from "react";
import { FiInfo, FiUsers } from "react-icons/fi";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import EmptyState from "../components/ui/EmptyState";
import OrganigrammeViewer from "../components/org/OrganigrammeViewer";
import { structureApi, OrganigrammeData } from "../api/structure";
import { useAuth } from "../contexts/AuthContext";
import { isCollaborateurUser } from "../utils/roles";
import toast from "react-hot-toast";

const SOURCE_LABELS: Record<OrganigrammeData["source"], string> = {
  manual: "Organigramme officiel",
  employees: "Registre du personnel",
  empty: "Non renseigné",
};

const OrganigrammePage: React.FC = () => {
  const { user } = useAuth();
  const isCollab = isCollaborateurUser(user);
  const [data, setData] = useState<OrganigrammeData | null>(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await structureApi.getOrganigramme();
      setData(res.data.data);
    } catch {
      toast.error("Impossible de charger l'organigramme");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div
        style={{ padding: "3rem", display: "flex", justifyContent: "center" }}
      >
        <LoadingSpinner size={36} />
      </div>
    );
  }

  return (
    <div style={{ padding: "2rem", maxWidth: 1200, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "1.5rem",
          flexWrap: "wrap",
          gap: "1rem",
        }}
      >
        <div>
          <h1
            style={{
              margin: "0 0 0.35rem",
              fontSize: "var(--font-size-2xl)",
              fontWeight: 700,
              color: "var(--color-primary)",
            }}
          >
            Organigramme
          </h1>
          <p
            style={{
              margin: 0,
              fontSize: "var(--font-size-sm)",
              color: "var(--color-text-secondary)",
            }}
          >
            {data?.company_name || "Votre entreprise"}
            {" - "}
            consultation en lecture seule
          </p>
        </div>
        {data?.source && data.source !== "empty" && (
          <Badge variant="info" size="sm">
            {SOURCE_LABELS[data.source]}
          </Badge>
        )}
      </div>

      {!data?.organigramme ? (
        <Card padding="2rem">
          <EmptyState
            icon={<FiUsers size={32} />}
            title="Organigramme non disponible"
            description={
              isCollab
                ? "Le service RH n'a pas encore publié l'organigramme. Contactez votre responsable RH."
                : "Configurez l'organigramme dans Ma structure ou renseignez les managers dans le registre du personnel."
            }
          />
        </Card>
      ) : (
        <Card padding="1.5rem">
          <OrganigrammeViewer organigramme={data.organigramme} />
        </Card>
      )}

      <div
        style={{
          marginTop: "1.25rem",
          padding: "1rem",
          background: "var(--color-info-light)",
          borderRadius: "var(--radius-lg)",
          fontSize: "var(--font-size-sm)",
          color: "var(--color-text-secondary)",
          display: "flex",
          gap: "0.5rem",
          alignItems: "flex-start",
        }}
      >
        <FiInfo size={16} style={{ flexShrink: 0, marginTop: 2 }} />
        <p style={{ margin: 0, lineHeight: 1.6 }}>
          {data?.source === "employees"
            ? "Cet organigramme est généré automatiquement à partir du registre du personnel (liens manager / collaborateur)."
            : data?.source === "manual"
              ? "Organigramme publié par le service RH. Les modifications sont réservées aux profils RH."
              : "L'organigramme sera affiché dès qu'il sera configuré par le service RH."}
        </p>
      </div>
    </div>
  );
};

export default OrganigrammePage;
